import React, { Fragment } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Check } from "lucide-react";
import { riskApiService } from "../../services/RiskService";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { Column } from "primereact/column";
import { ConvertToISortModel, numberFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { Col, FormGroup, Label, Row } from "reactstrap";
import { AppCard, DataTableToolbar } from "../../components/ui";
import { IndividualRiskMatrix } from "../../models/IndividualRiskMatrix";
import { toastService } from "../../services/toastService";

interface IndividualRiskMatrixProps {
    onHide: () => void;
}

interface IndividualRiskMatrixState {
    riskParams: IndividualRiskMatrix[];
    selectedRiskParam: IndividualRiskMatrix | null;
    editedParamWeight: string;
    inEditMode: boolean;
    visibleEditDialog: boolean;
    visibleConfirmDialog: boolean;
    isLoading: boolean;
    totalRecords: number;
}

class IndividualRiskMatrixForm extends React.Component<IndividualRiskMatrixProps, IndividualRiskMatrixState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: IndividualRiskMatrixProps | Readonly<IndividualRiskMatrixProps>) {
        super(props);
        this.state = {
            riskParams: [],
            selectedRiskParam: null,
            editedParamWeight: "",
            inEditMode: true,
            visibleEditDialog: false,
            visibleConfirmDialog: false,
            isLoading: false,
            totalRecords: 0,
        };
    }

    // async componentDidMount() {
    //     this.setState({ isLoading: true });
    //     this.getData({
    //         pageNumber: this.dataTable?.state.page!,
    //         pageSize: this.dataTable?.state.pageRowCount!,
    //         filters: [],
    //         sortings: []
    //     });
    //     this.setState({ isLoading: false });
    // }

    loadData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        const lookupResult = await riskApiService.getIndividualRiskMatrixPaged(searchRequest);
        this.setState({ isLoading: false });
        if (!lookupResult.success) {
            toastService.error(lookupResult.message);
            //this.dataTable!.reset();
            return;
        }
        this.setState({ riskParams: lookupResult.data, totalRecords: lookupResult.totalCount });
    };

    onFieldUpdate = (fieldName: string, value: any) => {
        this.setState({ editedParamWeight: value });
    };

    handleSubmit = async () => {
        let lookupResult;
        const selectedItem = this.state.selectedRiskParam as any;
        selectedItem.paramWeight = this.state.editedParamWeight;
        if (this.state.selectedRiskParam != null) {
            lookupResult = await riskApiService.updateIndividualRiskMatrixParam(selectedItem!);

            if (!lookupResult.success) {
                toastService.error(lookupResult.message);
                return;
            }
        }
        this.loadData({
            pageNumber: this.dataTable?.state.page!,
            pageSize: this.dataTable?.state.pageRowCount!,
            filters: [],
            sortings: ConvertToISortModel(this.dataTable?.state.multiSortMeta || []),
        });

        this.setState({ visibleEditDialog: false });
    };

    handleEdit = async (riskParam: IndividualRiskMatrix) => {
        this.setState({ editedParamWeight: "" });
        this.setState({ visibleEditDialog: true, inEditMode: true, selectedRiskParam: riskParam });
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => this.dataTable!.reset()} hasActiveFilters={hasActiveFilters} />;

    render() {
        const dialogHeader = this.state.inEditMode ? "Edit Risk Value" : "Add Risk Value";

        return (
            <Fragment>
                {/* Add Dialog */}
                <Dialog
                    header={dialogHeader}
                    visible={this.state.visibleEditDialog}
                    style={{ width: "40vw" }}
                    onHide={() => this.setState({ visibleEditDialog: false })}
                >
                    <div>
                        <AppCard className="!my-0">
                            {this.state.selectedRiskParam && (
                                <AppCard.Body>
                                    <FormGroup>
                                        <Label for="name">Param Name</Label>
                                        <InputText
                                            value={this.state.selectedRiskParam!.riskParamName}
                                            className={"form-control"}
                                            id="name"
                                            readOnly={true}
                                            required
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="paramWeight">Param Weight</Label>
                                        <InputText
                                            value={
                                                !this.state.editedParamWeight
                                                    ? this.state.selectedRiskParam.paramWeight.toString()
                                                    : this.state.editedParamWeight
                                            }
                                            className={"form-control"}
                                            id="paramWeight"
                                            readOnly={false}
                                            onChange={(e) => this.onFieldUpdate("paramWeight", e.target.value)}
                                            required
                                        />
                                    </FormGroup>
                                </AppCard.Body>
                            )}
                        </AppCard>
                    </div>
                    <div className="flex justify-end px-5 py-4">
                        <Button label="Save" icon={<Check size={16} />} loading={this.state.isLoading} onClick={this.handleSubmit} />
                    </div>
                </Dialog>

                {/* Risk Configure Dialog */}
                <AppCard className="!my-0">
                    <AppCard.Header title="Individual Risk Matrix" />

                    <AppCard.Body className="!p-0">
                        <ExtendedDataTable
                            header={this.renderHeader}
                            value={this.state.riskParams}
                            totalRecords={this.state.totalRecords}
                            onChange={this.loadData}
                            loading={this.state.isLoading}
                            ref={(ref) => (this.dataTable = ref)}
                            defaultSortOrder={SortOrderType.Ascending}
                            defaultSortField="riskParamName"
                        >
                            {/* <Column field="code" header="Code" filterMatchModeOptions={stringFilterMatchModeOptions}></Column> */}
                            <Column
                                header="Param Name"
                                field="riskParamName"
                                {...({ alwaysVisible: true } as any)}
                                body={(rowData: IndividualRiskMatrix) => <span>{rowData.riskParamName}</span>}
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                            ></Column>
                            <Column
                                header="Param Weight"
                                field="paramWeight"
                                {...({ alwaysVisible: true } as any)}
                                body={(rowData: IndividualRiskMatrix) => <span>{rowData.paramWeight}</span>}
                                // body={(rowData: IndividualRiskMatrix) =>
                                //         // <span>{rowData.paramWeight}</span>
                                //         <InputText value={rowData.paramWeight}
                                //         className={"form-control"} id="paramWeight"
                                //         //onChange={(e) => this.onFieldUpdate("lastName", e.target.value)}
                                //         readOnly={false} />
                                //     }
                                filterMatchModeOptions={numberFilterMatchModeOptions}
                            ></Column>
                            <Column
                                header=""
                                body={(rowData: IndividualRiskMatrix) => (
                                    <span>
                                        {/* p-button-raised p-button-rounded  p-button-text p-button-icon p-button */}
                                        <Button
                                            label="Edit"
                                            type="button"
                                            severity="success"
                                            onClick={() => this.handleEdit(rowData)}
                                            style={{ marginRight: "5px" }}
                                        ></Button>
                                        {/* <Button label="Delete" type="button" className="p-button p-button-danger" onClick={() => this.confirmDelete(rowData)}></Button> */}
                                    </span>
                                )}
                                headerStyle={{ textAlign: "center" }}
                                bodyStyle={{ textAlign: "center", overflow: "visible" }}
                            />
                        </ExtendedDataTable>
                    </AppCard.Body>
                </AppCard>
            </Fragment>
            // </Dialog>
        );
    }
}

export default IndividualRiskMatrixForm;
